import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import WorkflowStepDropZone from './WorkflowStepDropZone';
import WorkflowStepConnector from './WorkflowStepConnector';
import WorkflowCopyModeToast from './WorkflowCopyModeToast';
import GroupedWorkflowStepCard from './GroupedWorkflowStepCard';
import { buildDisplaySteps } from '../../utils/workflowStepGrouping';
import { WORKFLOW_MAIN_CARD_CENTER_X, WORKFLOW_MAIN_COLUMN_WIDTH } from '../../utils/workflowCardAnchors';
import { isConditionStep, isTerminalConditionBranchStep, computeConditionBranchGraphWidthForStep } from '../../utils/workflowConditionUtils';
import { normalizeBranchGraphHeight } from '../../utils/workflowConditionBranchGraphHeight';
import {
  buildMovingIds,
  canDropStep,
  copyStep,
  copyStepBlock,
  getStepLabel,
  moveStep,
  moveStepBlock,
  pasteStepBlock,
} from '../../utils/workflowStepReorder';

/**
 * Renders workflow action steps with move/copy/paste drop zones and drag-handle support.
 */
const WorkflowBuilderStepList = ({
  steps,
  theme = 'dark',
  selectedStepId,
  onStepsChange,
  onAddStep,
  onRemove,
  renderStep,
  renderConditionBranch = null,
  readOnly = false,
  pathStepStates = null,
  advancedMode = false,
  onEditGroup,
  selectedGroupId = null,
  columnWidth = WORKFLOW_MAIN_COLUMN_WIDTH,
  connectorCenterX = null,
  columnOffset = 0,
}) => {
  const [interaction, setInteraction] = useState(null);
  const [hoveredDropIndex, setHoveredDropIndex] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [copyMeta, setCopyMeta] = useState(null);
  const [branchGraphHeights, setBranchGraphHeights] = useState({});
  const branchHeightHandlersRef = useRef({});

  const setBranchGraphHeight = useCallback((stepId, height) => {
    const nextHeight = normalizeBranchGraphHeight(height);
    setBranchGraphHeights((previous) => {
      const current = previous[stepId] ?? 0;
      if (Math.abs(current - nextHeight) <= 1) return previous;
      return { ...previous, [stepId]: nextHeight };
    });
  }, []);

  const getBranchGraphHeightChangeHandler = useCallback((stepId) => {
    if (!branchHeightHandlersRef.current[stepId]) {
      branchHeightHandlersRef.current[stepId] = (height) => setBranchGraphHeight(stepId, height);
    }
    return branchHeightHandlersRef.current[stepId];
  }, [setBranchGraphHeight]);

  const isPasteMode = interaction?.type === 'paste';
  const isMoveMode = interaction?.type === 'move' || interaction?.type === 'move-block';
  const movingIds = interaction?.movingIds || [];
  const showMoveDropZones = !readOnly && isMoveMode;

  const cancelInteraction = useCallback(() => {
    setInteraction(null);
    setHoveredDropIndex(null);
    setCopyMeta(null);
  }, []);

  useEffect(() => {
    if (!isPasteMode && !isMoveMode) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') cancelInteraction();
    };
    const handleClickOutside = (e) => {
      if (e.target.closest('[data-workflow-step-card]')) return;
      if (e.target.closest('[data-testid^="workflow-drop-zone"]')) return;
      if (e.target.closest('[data-testid^="workflow-paste-target"]')) return;
      if (e.target.closest('[data-testid="workflow-copy-mode-toast"]')) return;
      if (e.target.closest('[data-testid^="step-context-menu"]')) return;
      cancelInteraction();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPasteMode, isMoveMode, cancelInteraction]);

  const movingLabel = useMemo(() => {
    if (!interaction || isPasteMode) return null;
    if (movingIds.length === 1) {
      const step = steps.find((s) => s.id === movingIds[0]);
      return `Moving "${getStepLabel(step)}"`;
    }
    return `Moving ${movingIds.length} actions`;
  }, [interaction, isPasteMode, movingIds, steps]);

  const resolvedColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH;
  const laneCenterX = WORKFLOW_MAIN_CARD_CENTER_X;
  const globalLaneCenterX = columnOffset + laneCenterX;

  const wrapStepCard = (content) => content;

  const handleDropAt = useCallback((targetIndex) => {
    if (isPasteMode && clipboard?.length) {
      if (targetIndex < 0 || targetIndex > steps.length) return;
      onStepsChange(pasteStepBlock(steps, clipboard, targetIndex));
      toast.success(clipboard.length === 1 ? 'Action pasted' : 'Actions pasted');
      cancelInteraction();
      return;
    }

    if (!movingIds.length) return;
    if (!canDropStep(steps, movingIds, targetIndex)) return;

    if (interaction.type === 'move-block') {
      onStepsChange(moveStepBlock(steps, interaction.sourceStepId, targetIndex));
    } else {
      onStepsChange(moveStep(steps, movingIds[0], targetIndex));
    }
    cancelInteraction();
  }, [isPasteMode, clipboard, steps, movingIds, interaction, onStepsChange, cancelInteraction]);

  const startMove = useCallback((stepId, includeFollowing = false) => {
    const ids = buildMovingIds(steps, stepId, includeFollowing);
    if (!ids.length) return;
    setCopyMeta(null);
    setInteraction({
      type: includeFollowing ? 'move-block' : 'move',
      sourceStepId: stepId,
      movingIds: ids,
    });
  }, [steps]);

  const startPaste = useCallback(() => {
    if (!clipboard?.length) return;
    setCopyMeta((prev) => prev || {
      label: clipboard.length === 1 ? getStepLabel(clipboard[0]) : null,
      count: clipboard.length,
    });
    setInteraction({ type: 'paste', movingIds: [] });
  }, [clipboard]);

  const handleCopy = useCallback((stepId, includeFollowing = false) => {
    const block = includeFollowing
      ? copyStepBlock(steps, stepId)
      : [copyStep(steps.find((s) => s.id === stepId))].filter(Boolean);
    if (!block.length) return;

    setClipboard(block);
    setCopyMeta({
      label: includeFollowing ? null : getStepLabel(block[0]),
      count: block.length,
    });
    setInteraction({ type: 'paste', movingIds: [] });
  }, [steps]);

  const handleContextAction = useCallback((action, stepId) => {
    switch (action) {
      case 'copy':
        handleCopy(stepId, false);
        break;
      case 'copy-block':
        handleCopy(stepId, true);
        break;
      case 'move':
        startMove(stepId, false);
        break;
      case 'move-block':
        startMove(stepId, true);
        break;
      case 'paste':
        startPaste();
        break;
      default:
        break;
    }
  }, [handleCopy, startMove, startPaste]);

  const handleDragStart = useCallback((stepId) => {
    if (readOnly) return;
    startMove(stepId, false);
  }, [readOnly, startMove]);

  const getDropZoneLabel = (index, valid) => {
    if (!valid) return 'Not allowed';
    return 'Move here';
  };

  const renderMoveDropZone = (index) => {
    if (!showMoveDropZones) return null;

    const valid = canDropStep(steps, movingIds, index);

    return (
      <WorkflowStepDropZone
        key={`drop-${index}`}
        index={index}
        label={getDropZoneLabel(index, valid)}
        isValid={valid}
        isHovered={hoveredDropIndex === index}
        showPasteIcon={false}
        onClick={() => handleDropAt(index)}
        onDragEnter={(e) => {
          e.preventDefault();
          setHoveredDropIndex(index);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setHoveredDropIndex(index);
        }}
        onDragLeave={() => setHoveredDropIndex((current) => (current === index ? null : current))}
        onDrop={(e) => {
          e.preventDefault();
          if (valid) handleDropAt(index);
        }}
      />
    );
  };

  const renderConnector = (index, addIndex) => {
    if (pathStepStates && steps[index] && steps[index + 1] && pathStepStates[steps[index].id] && pathStepStates[steps[index + 1].id]) {
      return (
        <div className="relative flex h-[72px] items-center justify-center" data-testid={`path-connector-${steps[index].id}`}>
          <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-gradient-to-b from-emerald-500/30 via-emerald-500/55 to-emerald-500/30" />
        </div>
      );
    }

    if (showMoveDropZones) {
      return renderMoveDropZone(index);
    }

    return (
      <WorkflowStepConnector
        connectorTestId={`workflow-connector-${index}`}
        addTestId={`add-step-between-${addIndex}`}
        pasteTestId={`workflow-paste-target-${index}`}
        insertionPointId={`insertion-${addIndex}`}
        readOnly={readOnly}
        columnWidth={resolvedColumnWidth}
        centerX={laneCenterX}
        onAdd={() => onAddStep(addIndex)}
        showPaste={isPasteMode}
        onPaste={() => handleDropAt(index)}
        pasteValid={index >= 0 && index <= steps.length}
      />
    );
  };

  const handleStepContextAction = useCallback((action, stepId) => {
    if (action === 'delete') {
      onRemove?.(stepId);
      return;
    }
    if (action === 'delete-block') {
      const startIndex = steps.findIndex((s) => s.id === stepId);
      if (startIndex >= 0) {
        onStepsChange(steps.slice(0, startIndex));
      }
      return;
    }
    if (action === 'notes') {
      return;
    }
    handleContextAction(action, stepId);
  }, [steps, onRemove, onStepsChange, handleContextAction]);

  const { display, warnings } = useMemo(
    () => buildDisplaySteps(steps, { advancedMode }),
    [steps, advancedMode],
  );

  const stepHelpers = {
    isMovingStep: (stepId) => movingIds.includes(stepId),
    isSelected: (stepId) => selectedStepId === stepId,
    isGroupSelected: (groupId) => selectedGroupId === groupId,
    isMoveMode: showMoveDropZones,
    isPasteMode,
    hasClipboard: Boolean(clipboard?.length),
    onDragHandleStart: (stepId) => handleDragStart(stepId),
    onContextMenuAction: handleStepContextAction,
  };

  return (
    <div
      className="relative shrink-0"
      style={{ width: resolvedColumnWidth, marginLeft: columnOffset }}
      data-testid="workflow-step-list"
      data-workflow-main-column
      data-workflow-main-lane-column
      data-workflow-action-lane
      data-workflow-action-lane-center-x={laneCenterX}
      data-workflow-main-lane-x={globalLaneCenterX}
      data-workflow-main-global-center-x={globalLaneCenterX}
      data-workflow-action-lane-width={resolvedColumnWidth}
      data-workflow-main-chain-width={resolvedColumnWidth}
      data-workflow-action-lane-offset={columnOffset}
      data-workflow-main-chain-left={columnOffset}
      data-workflow-main-center-x={laneCenterX}
    >
      {isPasteMode && clipboard?.length ? (
        <WorkflowCopyModeToast
          label={copyMeta?.label || getStepLabel(clipboard[0])}
          count={copyMeta?.count || clipboard.length}
          onDismiss={cancelInteraction}
        />
      ) : null}

      {showMoveDropZones && movingLabel ? (
        <div
          className={cn(
            'mb-3 rounded-lg border px-3 py-2 text-xs font-medium',
            theme === 'dark'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700',
          )}
          data-testid="workflow-moving-banner"
        >
          {movingLabel}
          <span className="ml-2 text-slate-500">Click a drop zone or press Esc to cancel</span>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800" data-testid="workflow-backend-warning">
          {warnings[0].message}
        </div>
      ) : null}

      {renderConnector(0, -1)}

      {display.map((item) => {
        if (item.type === 'group') {
          return (
            <React.Fragment key={item.groupId}>
              {wrapStepCard(
                <GroupedWorkflowStepCard
                  groupItem={item}
                  isSelected={stepHelpers.isGroupSelected(item.groupId)}
                  isMoving={item.stepIds.some((id) => stepHelpers.isMovingStep(id))}
                  readOnly={readOnly}
                  hasClipboard={stepHelpers.hasClipboard}
                  onEdit={onEditGroup}
                  onRemove={(group) => onRemove?.(group.stepIds[0])}
                  onDragHandleStart={stepHelpers.onDragHandleStart}
                  onContextMenuAction={handleStepContextAction}
                />,
              )}
              {renderConnector(item.endIndex + 1, item.endIndex)}
            </React.Fragment>
          );
        }

        const step = item.step;
        const index = item.startIndex;
        const isCondition = isConditionStep(step);
        const branchGraphHeight = branchGraphHeights[step.id] ?? 0;
        const branchContent = renderConditionBranch && isCondition
          ? renderConditionBranch(step, index, stepHelpers, {
            onGraphHeightChange: getBranchGraphHeightChangeHandler(step.id),
          })
          : null;
        const branchGraphWidth = isCondition && branchContent
          ? computeConditionBranchGraphWidthForStep(step)
          : 0;
        const branchGraphLeft = branchGraphWidth
          ? laneCenterX - branchGraphWidth / 2
          : 0;
        const suppressPostStepConnector = isTerminalConditionBranchStep(
          step,
          index,
          steps,
          Boolean(renderConditionBranch && branchContent),
        );

        return (
          <React.Fragment key={step.id}>
            <div
              className="relative"
              data-workflow-condition-step-anchor={isCondition ? 'true' : undefined}
            >
              {wrapStepCard(renderStep(step, index, stepHelpers))}
              {branchContent ? (
                <div
                  className="pointer-events-auto absolute z-[1] overflow-visible"
                  style={{
                    top: '100%',
                    left: branchGraphLeft,
                    width: branchGraphWidth,
                    minWidth: branchGraphWidth,
                  }}
                  data-workflow-branch-graph-layer
                  data-workflow-condition-branch-slot
                  data-workflow-condition-branch-width={branchGraphWidth}
                  data-workflow-condition-graph-left={branchGraphLeft}
                >
                  {branchContent}
                </div>
              ) : null}
            </div>
            {branchContent ? (
              <div
                aria-hidden="true"
                className="shrink-0"
                style={{ height: branchGraphHeight }}
                data-workflow-branch-graph-spacer
              />
            ) : null}
            {!suppressPostStepConnector ? renderConnector(index + 1, index) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WorkflowBuilderStepList;
